import React, { useState } from 'react';
import Modal from 'react-modal';
import { useTranslation } from 'react-i18next';

export interface Slide {
    title: string;
    description: string;
    image?: string;
    isAcceptable?: boolean;
}

interface GuidanceModalProps {
    isOpen: boolean;
    onClose: () => void;
    slides: Slide[];
    isRectanglePlaceholder?: boolean;
    hideUnacceptableBadge?: boolean;
}

export const GuidanceModal: React.FC<GuidanceModalProps> = ({ isOpen, onClose, slides, isRectanglePlaceholder, hideUnacceptableBadge }) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<'acceptable' | 'unacceptable'>('acceptable');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);

    React.useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 767);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Reset onto first tab when opened
    React.useEffect(() => {
        if (isOpen) {
            setActiveTab('acceptable');
        }
    }, [isOpen]);

    if (!slides || slides.length === 0) return null;

    const acceptableSlides = slides.filter(s => s.isAcceptable);
    const unacceptableSlides = slides.filter(s => !s.isAcceptable);

    const currentSlides = activeTab === 'acceptable' ? acceptableSlides : unacceptableSlides;

    const customStyles = {
        overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '0' : '20px'
        },
        content: {
            position: isMobile ? ('absolute' as 'absolute') : ('relative' as 'relative'),
            inset: isMobile ? '0' : 'auto',
            border: 'none',
            background: '#f9f9f9',
            overflow: 'hidden', 
            borderRadius: isMobile ? '0' : '12px',
            outline: 'none',
            padding: '0',
            width: isMobile ? '100vw' : '100%',
            height: isMobile ? '100vh' : 'auto',
            maxWidth: isMobile ? '100vw' : '650px',
            maxHeight: isMobile ? '100vh' : '90vh', // increased max height
            display: 'flex',
            flexDirection: 'column' as 'column',
            boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            shouldCloseOnOverlayClick={true}
            style={customStyles}
            contentLabel="Guidance Modal"
            ariaHideApp={false}
        >
            {/* Header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #eaeaea', background: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 10 }}>
                <h3 style={{ margin: 0, fontSize: '20px', color: '#111', fontWeight: 700 }}>{t('Guidance')}</h3>
                <button onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: '28px', cursor: 'pointer', lineHeight: '1', color: '#666' }}>&times;</button>
            </div>
            
            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid #ddd', background: '#fff', zIndex: 10 }}>
                <button 
                    onClick={() => setActiveTab('acceptable')}
                    style={{
                        flex: 1,
                        padding: '14px 0',
                        fontSize: '16px',
                        fontWeight: 600,
                        background: activeTab === 'acceptable' ? '#fff' : '#fafafa',
                        color: activeTab === 'acceptable' ? '#28a745' : '#666',
                        border: 'none',
                        borderBottom: activeTab === 'acceptable' ? '3px solid #28a745' : '3px solid transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                    }}
                >
                    {t('Acceptable')}
                </button>
                <button 
                    onClick={() => setActiveTab('unacceptable')}
                    style={{
                        flex: 1,
                        padding: '14px 0',
                        fontSize: '16px',
                        fontWeight: 600,
                        background: activeTab === 'unacceptable' ? '#fff' : '#fafafa',
                        color: activeTab === 'unacceptable' ? '#dc3545' : '#666',
                        border: 'none',
                        borderBottom: activeTab === 'unacceptable' ? '3px solid #dc3545' : '3px solid transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                    }}
                >
                    {t('Not Acceptable')}
                </button>
            </div>

            {/* Scrollable Content Area */}
            <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '24px', display: 'flex', flexDirection: 'column', gap: '30px' }}>
                {currentSlides.map((slide, idx) => (
                    <div key={idx} style={{ 
                        background: '#fff', 
                        borderRadius: '10px', 
                        padding: '20px', 
                        boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        position: 'relative'
                    }}>
                        {/* Status Icon Indicator */}
                        {!(hideUnacceptableBadge && activeTab === 'unacceptable') && (
                            <div style={{
                                position: 'absolute',
                            top: '-12px',
                            right: '-12px',
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: activeTab === 'acceptable' ? '#28a745' : '#dc3545',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            fontSize: '18px',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                        }}>
                                {activeTab === 'acceptable' ? '✓' : '✗'}
                            </div>
                        )}

                        <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {slide.image ? (
                                <img src={slide.image} alt="step" style={{ maxWidth: '100%', maxHeight: '180px', objectFit: 'contain', borderRadius: '8px' }} />
                            ) : (
                                <div style={{ 
                                    width: isRectanglePlaceholder ? '200px' : '140px', 
                                    height: isRectanglePlaceholder ? '120px' : '140px', 
                                    background: '#f4f5f7', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    borderRadius: isRectanglePlaceholder ? '8px' : '50%', 
                                    color: '#bbb', 
                                    border: '2px dashed #ddd' 
                                }}>
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                        <polyline points="21 15 16 10 5 21"></polyline>
                                    </svg>
                                </div>
                            )}
                        </div>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '18px', color: '#222', fontWeight: 600 }}>
                            {slide.title}
                        </h4>
                        <p style={{ margin: 0, fontSize: '15px', color: '#555', lineHeight: '1.6' }}>
                            {slide.description}
                        </p>
                    </div>
                ))}

                {currentSlides.length === 0 && (
                    <div style={{ padding: '40px 20px', textAlign: 'center', color: '#888' }}>
                        {t('No examples provided for this category.')}
                    </div>
                )}
            </div>

            {/* Footer */}
            <div style={{ padding: '16px 20px', borderTop: '1px solid #eaeaea', background: '#fff', display: 'flex', justifyContent: 'center', zIndex: 10 }}>
                <div className="cco_btn cco_btn_mybtn">
                    <button onClick={onClose} type="button">
                        {t('Close')}
                    </button>
                </div>
            </div>
        </Modal>
    );
};
